import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  useEffect(() => {
    // Wait for auth to finish loading before making any redirect decision
    if (loading) return;

    // Mark that we've checked auth
    setHasCheckedAuth(true);

    // Only redirect if definitively not logged in AND we've waited for auth
    if (!user) {
      // Check if this might be an OAuth callback - give it a bit more time
      const isOAuthCallback = location.search.includes('code=') ||
                              location.hash.includes('access_token');
      if (isOAuthCallback) {
        // OAuth callback - don't redirect immediately, the session will be established
        return;
      }
      navigate("/auth", { replace: true });
    }
  }, [user, loading, navigate, location]);

  if (loading || !hasCheckedAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return <>{children}</>;
}
